import React, { useEffect, useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

export interface IconSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface IconSelectProps {
  value: string;
  options: IconSelectOption[];
  onChange: (value: string) => void;
  ariaLabel: string;
  id?: string;
}

/** A listbox dropdown whose options carry an icon (a native <select> cannot render one). */
export const IconSelect: React.FC<IconSelectProps> = ({ value, options, onChange, ariaLabel, id }) => {
  const autoId = useId();
  const listId = `${id ?? autoId}-list`;
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selectedIndex = Math.max(0, options.findIndex((o) => o.value === value));
  const [active, setActive] = useState(selectedIndex);
  const selected = options[selectedIndex];

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const choose = (index: number) => {
    onChange(options[index].value);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setActive(selectedIndex); setOpen(true); return; }
      setActive((i) => (e.key === 'ArrowDown' ? Math.min(options.length - 1, i + 1) : Math.max(0, i - 1)));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      choose(active);
    }
  };

  return (
    <div ref={rootRef} className="relative" onKeyDown={onKeyDown}>
      <button
        type="button"
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => { setActive(selectedIndex); setOpen((o) => !o); }}
        className="w-full flex items-center gap-2 text-left cursor-pointer"
        style={{
          padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r-8)',
          background: 'var(--white)', color: 'var(--ink-900)', fontSize: 14, fontFamily: 'var(--font)',
        }}
      >
        <span className="flex items-center gap-2 flex-1 min-w-0">
          {selected?.icon}
          <span className="truncate">{selected?.label}</span>
        </span>
        <ChevronDown size={14} style={{ color: 'var(--ink-500)' }} />
      </button>
      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute left-0 right-0 m-0 p-1 list-none"
          style={{
            top: 'calc(100% + 4px)', zIndex: 20000, background: 'var(--white)',
            border: '1px solid var(--line)', borderRadius: 'var(--r-8)', boxShadow: 'var(--sh-2)',
          }}
        >
          {options.map((o, i) => (
            <li
              key={o.value}
              role="option"
              aria-selected={o.value === value}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(i)}
              className="flex items-center gap-2 cursor-pointer"
              style={{
                padding: '7px 9px', borderRadius: 'var(--r-6)', fontSize: 14,
                background: i === active ? 'var(--surface-2)' : 'transparent',
              }}
            >
              <span className="flex items-center gap-2 flex-1 min-w-0">
                {o.icon}
                <span className="truncate">{o.label}</span>
              </span>
              {o.value === value && <Check size={14} style={{ color: 'var(--tenant-primary)' }} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
